import { Component, type ErrorInfo, type ReactNode } from "react"

/**
 * Keeps a failing part of the page (a lazy chunk that didn't load, a widget
 * that crashed) from taking the whole app down: it shows `fallback` instead.
 */
export class ErrorBoundary extends Component<{
  readonly fallback: (error: Error, reset: () => void) => ReactNode
  readonly children: ReactNode
}, { readonly error: Error | null }> {
  override state: { readonly error: Error | null } = { error: null }

  static getDerivedStateFromError(error: unknown): { readonly error: Error } {
    return { error: error instanceof Error ? error : new Error(String(error)) }
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    console.error("A part of the page failed:", error, info.componentStack)
  }

  override render(): ReactNode {
    return this.state.error ? this.props.fallback(this.state.error, () => this.setState({ error: null })) : this.props.children
  }
}
